import styled from "styled-components";

/**
 * Outer wrapper — anchors the strip in its parent and lifts z-index while
 * the overflow menu is open so the dropdown clears sibling rows.
 */
export const Wrapper = styled.div`
  position: relative;
  height: 100%;
  z-index: ${({ $menuOpen, theme }) =>
    $menuOpen ? theme.zIndex.dropdown : theme.zIndex.above};
`;

/**
 * The pill background that holds the icons. `align` controls which edge of the
 * parent it anchors to and the visual order of icons within it.
 *
 * align="right" → row-reverse, anchored to the right (track-row behavior)
 * align="left"  → row, anchored to the left (header-widget behavior)
 */
export const Strip = styled.div`
  position: relative;
  display: flex;
  flex-direction: ${({ $align }) =>
    $align === "right" ? "row-reverse" : "row"};
  align-items: center;
  gap: 0;
  border-radius: ${({ theme }) => theme.br.md};
  padding: ${({ theme }) => theme.spacing[1]};
  background: ${({ $revealed, $transparent, theme }) =>
    $transparent
      ? "transparent"
      : $revealed
      ? theme.backgroundLight
      : "transparent"};
  transition: background 0.2s ease;
  z-index: ${({ theme }) => theme.zIndex.top};
  height: fit-content;
`;

/**
 * Shared absolute-positioning mixin applied to whichever element acts as the
 * outermost flow container — `Strip` when there is no inline label, or
 * `StripRow` when an inline label pill rides alongside the strip.
 */
const absolutePositioning = ({ $absolute, $align, theme }) =>
  $absolute
    ? $align === "right"
      ? `position: absolute; right: -${theme.spacing[3]}; top: 50%; transform: translateY(-50%);`
      : `position: absolute; left: -${theme.spacing[3]}; top: 50%; transform: translateY(-50%);`
    : "";

/**
 * Slides the always-visible action icons in/out via max-width transition.
 * Used when `revealOnHover` is set (track-row behavior). For the header-widget
 * use case the icons should remain visible, so this collapses to a no-op
 * (max-width: none, opacity: 1) via the $visible flag.
 */
export const SlidingIcons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  gap: 0;
  overflow: hidden;
  max-width: ${({ $visible, $animate }) =>
    $animate ? ($visible ? "500px" : "0") : "none"};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: max-width 0.5s ease;
`;

/**
 * Label pill that slides out adjacent to the strip on hover (used when
 * `labelMode="inline"`). Absolutely positioned inside StripRow so its
 * expansion does not push sibling content in the parent layout — it overlays
 * whatever sits next to the strip, like a tooltip. The pill's outer edge sits
 * behind the strip's rounded chip (via the `spacing[6]` margin/padding trick,
 * same as before) so the inner corners blend cleanly. `$align` controls which
 * side of the strip the pill attaches to.
 */
export const LabelPill = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: ${({ theme }) => theme.backgroundLight};
  border-radius: ${({ theme }) => theme.br.md};
  ${({ $align, theme }) =>
    $align === "right"
      ? `
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        right: 100%;
        margin-right: -${theme.spacing[6]};
        padding-right: ${theme.spacing[6]};
      `
      : `
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        left: 100%;
        margin-left: -${theme.spacing[6]};
        padding-left: ${theme.spacing[6]};
      `}
  z-index: ${({ theme }) => theme.zIndex.raised};
  display: flex;
  align-items: center;
  overflow: hidden;
  max-width: ${({ $visible }) => ($visible ? "240px" : "0")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: max-width 0.3s ease, opacity 0.2s ease;
  white-space: nowrap;
  pointer-events: none;

  span {
    padding: 0 ${({ theme }) => theme.spacing[3]};
    font-family: ${({ theme }) => theme.p3r.ff};
    font-size: ${({ theme }) => theme.p3r.fs};
    color: ${({ theme }) => theme.textLight};
  }
`;

/**
 * Flex row that lays out the strip + (optional) label pill on the same axis.
 * For align="right" the pill sits to the left of the strip; for align="left"
 * it sits to the right. `position: relative` anchors the absolute LabelPill.
 */
export const StripRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  ${absolutePositioning}
`;
